import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import {
  CircularProgress,
  Container,
  Portal,
  IconButton,
  Badge,
  Button,
} from "@mui/material";
import { Typography, TextField, Autocomplete } from "@mui/material";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import {
  List,
  ViewModule,
  Link,
  Create,
  Event,
  FindInPage,
  SettingsInputAntenna,
  Add,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { CopyToClipboard } from "react-copy-to-clipboard";
import ImageList from "../../../components/imageList/ImageList";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ResponsiveDialogManager from "../../../components/dialog/ResponsiveDialogManager";
import MyDesignZones from "../../../components/createMyDesign/MyDesignZones";
import { useViewport } from "../../../helpers";
import { useNavigate } from "react-router-dom/dist";
import {
  closeCreateDesignZones,
  openBackdrop,
  openCreateDesignZones,
} from "../../../reducers/myDesigns/myDesignsSlice";
import { sendNotification } from "../../../reducers/myDesigns/myDesignsThunks";

const PREFIX = "Zones";

const classes = {
  root: `${PREFIX}-root`,
  zonesAutocomplete: `${PREFIX}-zonesAutocomplete`,
  autocomplete: `${PREFIX}-autocomplete`,
  toggleButtonHeight: `${PREFIX}-toggleButtonHeight`,
  containerMargin: `${PREFIX}-containerMargin`,
  dataGridMargin: `${PREFIX}-dataGridMargin`,
  createZoneMargin: `${PREFIX}-createZoneMargin`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`&.${classes.root}`]: {
    padding: 0,
  },

  [`& .${classes.zonesAutocomplete}`]: {
    display: "flex",
    flexDirection: "row",
    maxWidth: "350px",
    marginBottom: "2rem",
  },

  [`& .${classes.autocomplete}`]: {
    width: "100%",
    maxWidth: "350px",
    marginTop: "1rem",
  },

  [`& .${classes.toggleButtonHeight}`]: {
    margin: "1rem 0 0 1rem",
    height: 55,
  },

  [`& .${classes.containerMargin}`]: {
    marginBottom: "2rem",
  },

  [`& .${classes.dataGridMargin}`]: {
    margin: "-2rem 1.5rem 5rem 1.5rem",
  },

  [`& .${classes.createZoneMargin}`]: {
    marginBottom: "1rem",
  },
}));

export default function Zones(props) {
  const { enqueueSnackbar } = useSnackbar();
  const history = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { width } = useViewport();
  const openCreate = useSelector(
    (state) => state.myDesigns.openCreateDesignZones
  );
  const [filter, setFilter] = useState([]);
  const [filteredZones, setFilteredZones] = useState([]);
  const [display, setDisplay] = useState("masonry");
  const [clipboard, setClipboard] = useState({
    value: "",
    copied: false,
  });

  function handleDisplay(event, newDisplay) {
    setDisplay(newDisplay);
  }

  useEffect(() => {
    if (props.zones && props.selectedTarget) {
      setFilteredZones(
        props.zones.filter((zone) => zone.target === props.selectedTarget.label)
      );
    }
  }, [props.zones, props.selectedTarget]);

  function handleFilterZones(value) {
    if (value.length === 0) {
      setFilter([]);
      setFilteredZones(props.zones);
    } else {
      setFilter(value);
      const filtered = value.flatMap((search) => {
        return props.zones.filter((zone) => {
          return (
            zone.label === search.label &&
            zone.id === search.id &&
            zone.target === search.target
          );
        });
      });
      setFilteredZones(filtered);
    }
  }

  const mySendNotification = async (tmpInfos) => {
    try {
      const thunk = await dispatch(
        sendNotification({
          frameId: tmpInfos.frameId,
          frameName: tmpInfos.title,
          label: tmpInfos.target,
          zoneName: tmpInfos.label,
        })
      );
      if (thunk.meta.requestStatus === "rejected")
        throw new Error("recovery error");
      enqueueSnackbar(t("toasts.notificationSent"), { variant: "success" });
    } catch (err) {
      enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
    }
  };

  const columns = (frameName, t) => {
    const maxCharacters = filteredZones.reduce((constant, current) =>
      constant > current.label.length ? constant : current.label.length
    );
    return [
      { field: "id", headerName: "Id", width: 88, type: "string" },
      {
        field: "target",
        headerName: t("myDesign.zones.target"),
        width: 120,
        type: "string",
      },
      {
        field: "label",
        headerName: t("myDesign.zones.label"),
        width: width > 768 ? maxCharacters * 8 : 140,
        type: "string",
      },
      {
        field: "actions",
        headerName: t("myDesign.zones.actions"),
        width: 320,
        renderCell: (params) => {
          const tmpInfos = { ...params.row, title: frameName };
          return (
            <StyledDiv>
              <CopyToClipboard
                onCopy={() =>
                  setClipboard({
                    value: `${process.env.REACT_APP_API_URL}/r/${params.row.uniqueKey}`,
                    copied: true,
                  })
                }
                text={`${process.env.REACT_APP_API_URL}/r/${params.row.uniqueKey}`}
              >
                <IconButton
                  className={classes.copyLinkButton}
                  aria-label="copy link"
                  color="primary"
                  onClick={(e) => {
                    enqueueSnackbar(t("toasts.copiedInClipboard"), {
                      variant: "info",
                    });
                    e.stopPropagation();
                  }}
                  size="large"
                >
                  <Link />
                </IconButton>
              </CopyToClipboard>
              {(tmpInfos.saveAccess === 1 ||
                tmpInfos.uploadAccess === 1 ||
                props.isAdmin) && (
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    dispatch(openBackdrop({ from: "modify", infos: tmpInfos }));
                  }}
                  size="large"
                >
                  <Create />
                </IconButton>
              )}
              {(tmpInfos.scheduleAccess === 1 || props.isAdmin) && (
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    history(
                      `/mydesign/${tmpInfos.frameId}/zone/${tmpInfos.id}`,
                      {
                        state: {
                          myDesign: tmpInfos,
                          index: 1,
                        },
                      }
                    );
                  }}
                  size="large"
                >
                  <Badge
                    badgeContent={tmpInfos.planifications}
                    color="secondary"
                    className={classes.badge}
                    overlap="rectangular"
                  >
                    <Event />
                  </Badge>
                </IconButton>
              )}
              {(tmpInfos.previewAccess === 1 || props.isAdmin) && (
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    window.open(tmpInfos.targetUrl, "_blank");
                  }}
                  size="large"
                >
                  <FindInPage />
                </IconButton>
              )}
              {(tmpInfos.notifyAccess === 1 || props.isAdmin) && (
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    mySendNotification(tmpInfos);
                  }}
                  size="large"
                >
                  <SettingsInputAntenna />
                </IconButton>
              )}
              <IconButton
                color="primary"
                onClick={(e) => {
                  history(`/mydesign/${tmpInfos.frameId}/zone/${tmpInfos.id}`, {
                    state: {
                      myDesign: tmpInfos,
                    },
                  });
                }}
                size="large"
              >
                <i className="fas fa-cog" style={{ color: "#D3AF5F" }} />
              </IconButton>
            </StyledDiv>
          );
        },
      },
    ];
  };

  return (
    <StyledDiv>
      <Container className={classes.root}>
        {props.zones && props.selectedTarget ? (
          <div className={classes.containerMargin}>
            <ResponsiveDialogManager
              open={openCreate}
              fullScreen
              renderer={
                <MyDesignZones
                  selectedTarget={props.selectedTarget}
                  frameId={props.frameId}
                />
              }
              title={t("homePage.myDesign.createDesignZonesModalTitle")}
              onClose={() => dispatch(closeCreateDesignZones())}
            />
            <Container disableGutters className={classes.zonesAutocomplete}>
              <Autocomplete
                label={t("myDesign.zones.filterLabel")}
                id="autocomplete-zones"
                className={classes.autocomplete}
                multiple
                options={props.zones.map((zone) => zone)}
                getOptionLabel={(option) =>
                  option.label !== undefined && option.label !== ""
                    ? option.label
                    : ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("myDesign.zones.filterLabel")}
                    variant="outlined"
                  />
                )}
                value={filter}
                onChange={(e, value) => handleFilterZones(value)}
              />
              {props.isAdmin && (
                <ToggleButtonGroup
                  value={display}
                  exclusive
                  onChange={handleDisplay}
                  aria-label="zones display"
                  className={classes.toggleButtonHeight}
                >
                  <ToggleButton value="masonry" aria-label="masonry">
                    <ViewModule />
                  </ToggleButton>
                  <ToggleButton value="list" aria-label="list">
                    <List />
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Container>
            {display === "masonry" ? (
              <ImageList
                myDesigns={filteredZones}
                zonesMode
                frameName={props.frameName}
                target={props.selectedTarget.label}
                parentUrl={props.parentUrl}
              />
            ) : (
              <Portal>
                <StyledDiv>
                  <div className={classes.dataGridMargin}>
                    {props.isAdmin && (
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.createZoneMargin}
                        startIcon={<Add />}
                        onClick={() => dispatch(openCreateDesignZones())}
                      >
                        {t("homePage.myDesign.addNewZones")}
                      </Button>
                    )}
                    <DataGrid
                      rows={filteredZones}
                      columns={columns(props.frameName, t)}
                      autoHeight
                      disableRowSelectionOnClick
                      pagination
                      initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                      }}
                      pageSizeOptions={[5, 10, 25, 50]}
                    />
                  </div>
                </StyledDiv>
              </Portal>
            )}
          </div>
        ) : (
          <Container>
            <div className="loading-container">
              <CircularProgress size={50} />
              <Typography>{t("loaders.fetching")}</Typography>
            </div>
          </Container>
        )}
      </Container>
    </StyledDiv>
  );
}
